import React, { useEffect, useRef, useState } from "react";
import { Field, PrimaryButton, SecondaryButton, inputClass, ringStyle } from "./shared";
import EvidenceUploader from "../rightsClaims/EvidenceUploader";
import { MESSAGES, fieldErrors, trackApi, trackErrorMessage } from "./trackApi";

// Moderation outcome (FR-CAT-011): the dialogs for the last two steps of a rejected track.
// - AppealDialog: reason (20 to 1,000 characters) and optional evidence (PDF/JPG/PNG, at most 5 files, 10 MB each).
//   One appeal per track; the server enforces that too.
// - DeleteTrackDialog: shown after an appeal was denied, when deleting is the only thing left to do.

export const APPEAL_REASON_MIN = 20;
export const APPEAL_REASON_MAX = 1000;
export const APPEAL_MAX_FILES = 5;

// The message to show under the reason box, or "" when it is fine. Checked on every keystroke.
export function appealReasonError(value) {
  const length = String(value || "").trim().length;
  if (!length) return "Please explain why this decision should be reviewed.";
  if (length < APPEAL_REASON_MIN) return `Your reason must be at least ${APPEAL_REASON_MIN} characters (${length}/${APPEAL_REASON_MIN}).`;
  if (length > APPEAL_REASON_MAX) return `Your reason can be at most ${APPEAL_REASON_MAX.toLocaleString()} characters.`;
  return "";
}

const appealErrorMessage = (e) => {
  const status = e?.response?.status;
  const message = e?.response?.data?.message;
  if (message && [400, 409].includes(status)) return message;
  return trackErrorMessage(e, MESSAGES[500]);
};

export function AppealDialog({ track, onCancel, onSubmitted }) {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [serverError, setServerError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (inputRef.current) inputRef.current.focus();
    return () => {
      mounted.current = false;
    };
  }, []);

  const problem = appealReasonError(reason);
  // The message shows as soon as the person starts typing, or after they leave the box, or after Submit was pressed.
  const shownProblem = serverError ? "" : (touched || reason) ? problem : "";
  const length = reason.trim().length;

  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (busy || problem) return;
    setBusy(true);
    setServerError("");
    try {
      const updated = await trackApi.appeal(track.id, reason.trim(), evidence);
      if (mounted.current) onSubmitted(updated);
    } catch (e) {
      if (!mounted.current) return;
      const fields = fieldErrors(e);
      setServerError((fields && (fields.reason || fields.evidence)) || appealErrorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4"
      role="dialog" aria-modal="true" aria-labelledby="appeal-title" data-testid="appeal-dialog"
    >
      <form onSubmit={submit} noValidate className="my-8 w-full max-w-lg rounded-2xl bg-white p-7 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="appeal-title" className="text-2xl font-bold text-gray-900">Appeal this decision</h2>
          <button type="button" onClick={onCancel} disabled={busy} aria-label="Close" className="text-2xl leading-none text-gray-500">×</button>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Your resubmission was rejected as well. You can appeal once, and the decision on your appeal is final.
        </p>
        {track.rejection_reason && (
          <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span className="font-semibold">Reason for rejection:</span> {track.rejection_reason}
          </p>
        )}

        <div className="mt-5 space-y-5">
          <Field label="Why should this decision be reviewed?" required error={shownProblem}>
            <textarea
              ref={inputRef} rows={5} value={reason} maxLength={APPEAL_REASON_MAX + 200}
              onChange={(e) => { setReason(e.target.value); setServerError(""); }}
              onBlur={() => setTouched(true)}
              aria-invalid={Boolean(shownProblem)} data-testid="appeal-reason"
              placeholder="Explain what was misunderstood or what has changed."
              className={inputClass(Boolean(shownProblem))} style={ringStyle}
            />
            <p className={`mt-1 text-right text-xs ${length > APPEAL_REASON_MAX ? "text-red-600" : "text-gray-500"}`} data-testid="appeal-count">
              {length}/{APPEAL_REASON_MAX.toLocaleString()}
            </p>
          </Field>

          <Field label="Evidence (optional)" hint="Contracts, licences or screenshots that support your appeal.">
            <EvidenceUploader value={evidence} onChange={(files) => { setEvidence(files); setServerError(""); }} disabled={busy} maxFiles={APPEAL_MAX_FILES} includeSize />
          </Field>
        </div>

        {serverError && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="appeal-error">{serverError}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <SecondaryButton onClick={onCancel} disabled={busy} data-testid="appeal-cancel">Cancel</SecondaryButton>
          <PrimaryButton type="submit" disabled={busy || Boolean(problem)} data-testid="appeal-submit">
            {busy ? "Submitting…" : "Submit Appeal"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export function DeleteTrackDialog({ track, busy, onCancel, onConfirm }) {
  const name = track.title || "this track";
  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center overflow-y-auto bg-black/40 px-4 py-4" role="dialog" aria-modal="true" aria-labelledby="delete-title" data-testid="delete-dialog">
      <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-7 shadow-xl">
        <div className="flex items-start justify-between gap-4">
          <h2 id="delete-title" className="text-2xl font-bold text-gray-900">Delete “{name}”?</h2>
          <button type="button" onClick={onCancel} disabled={busy} aria-label="Close" className="text-2xl leading-none text-gray-500">×</button>
        </div>
        <p className="mt-3 text-sm text-gray-600">
          Your appeal was denied, so this track cannot be resubmitted or appealed again. It will be removed from your catalogue.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <SecondaryButton onClick={onCancel} disabled={busy} data-testid="delete-cancel">Cancel</SecondaryButton>
          <button
            type="button" onClick={onConfirm} disabled={busy} data-testid="delete-confirm"
            className="rounded-full bg-red-600 px-7 py-3 font-semibold text-white hover:bg-red-700 disabled:opacity-60"
          >
            {busy ? "Deleting…" : "Delete Track"}
          </button>
        </div>
      </div>
    </div>
  );
}
